import { z } from 'zod';
import { PaddingSettingSchema, ShadowSettingKeySchema, TypographyTypeSchema } from '../setting';
import { TipTapContentSchema } from '../tiptap-content.schema';
import { TextAnimationSetSchema } from './animations';
import { BasePinComponentSchema, BasePinComponentSettingsSchema } from './base-pin-component';

const TextHorizontalAlignmentSchema = z.enum(['left', 'center', 'right', 'justify']);
export type TextHorizontalAlignmentKey = z.infer<typeof TextHorizontalAlignmentSchema>;

const TextVerticalAlignmentSchema = z.enum(['baseline', 'center', 'end']);
export type TextVerticalAlignmentKey = z.infer<typeof TextVerticalAlignmentSchema>;

export const TextPinComponentPinThemeSettingsSchema = z.object({
  backgroundColor: z.string().optional(),
  borderSize: z.number().optional(),
  borderColor: z.string().optional(),
  borderRadius: z.number().optional(),
  shadow: ShadowSettingKeySchema,
  padding: PaddingSettingSchema.optional(),
  lineHeight: z.number().optional(),
  paragraphSpacing: z.number().optional(),
  fontShadow: ShadowSettingKeySchema,
  fontFamily: z.string().optional(),
  fontSize: z.string().optional(),
  fontWeight: z.string().optional(),
  fontColor: z.string().optional(),
});
export type TextPinComponentPinThemeSettings = z.infer<typeof TextPinComponentPinThemeSettingsSchema>;

const TextSettingsSchema = BasePinComponentSettingsSchema.extend(
  TextPinComponentPinThemeSettingsSchema.extend(
    z.object({
      tipTapContent: TipTapContentSchema,
      textStyleKey: z.string().optional(),
      typographyKey: TypographyTypeSchema.optional(),
      horizontalAlignment: TextHorizontalAlignmentSchema.optional(),
      verticalAlignment: TextVerticalAlignmentSchema.optional(),
      /**
       * Animation configuration for this text component.
       *
       * The order field in an individual animation uses fractional indexing
       * scoped to the entire pin, allowing for coordinated animations across
       * multiple components within the same pin.
       */
      animations: TextAnimationSetSchema.optional(),
    }).shape
  ).shape
);

export type TextSettings = z.infer<typeof TextSettingsSchema>;

export const TextPinComponentTypeSchema = z.literal('TEXT');

export const TextPinComponentSchema = BasePinComponentSchema.extend(
  z.object({
    type: TextPinComponentTypeSchema,
    settings: TextSettingsSchema,
  }).shape
);
